import {
  GET_ALL_SESSIONS_LOADING,
  GET_ALL_SESSIONS_SUCCESS,
  GET_ALL_SESSIONS_FAIL,
  SYNC_SESSION_LOADING,
  SYNC_SESSION_SUCCESS,
  SYNC_SESSION_FAIL,
  UPDATE_SESSION_LOADING,
  UPDATE_SESSION_SUCCESS,
  UPDATE_SESSION_FAIL,
  CLEAR_SESSION_ERRORS,
  APPEND_BACKEND_EVENTS,
  HYDRATE_SESSION_TOOL_BUNDLES
} from '../actions/types';

const initialState = {
  // Session data
  allSessions: [], 
  allEvents: [], 
  allAggregatedEvents: [],
  // New: Single-bundle view per session (same shape as local currentTool)
  sessionToolBundles: [],
  
  // Loading states
  isLoading: false,
  isSyncing: false,
  isUpdating: false,
  
  // Error handling
  error: null,
  syncError: null,
  updateError: null,
  
  // Metadata
  lastFetch: null,
  syncedSessions: [],
  pendingSessions: [],
  // Optional backend-provided map of users to their projects
  userObj: null,
  
  // Statistics
  totalSessions: 0,
  totalEvents: 0
};

export default function sessionReducer(state = initialState, action) {
  switch (action.type) {
    // === FETCH ALL SESSIONS ===
    case GET_ALL_SESSIONS_LOADING:
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case GET_ALL_SESSIONS_SUCCESS:
      // Merge server events with any local optimistic ones to avoid losing them on immediate refresh
      const fetchedEvents = action.payload.events || [];
      const existing = state.allEvents || [];
      // Stable dedupe: ignore timestamp so identical tool-in-space within a session is considered one
      const byKey = new Map();
      const makeKey = (e) => `${e.sessionId || ''}|${(e.sessionType||'').toLowerCase()}|${(e.details?.tool||e.tool||'').toLowerCase()}`;
      [...existing, ...fetchedEvents].forEach(e => {
        const k = makeKey(e);
        if (!byKey.has(k)) byKey.set(k, e);
      });
      const normalizedEvents = Array.from(byKey.values());

      // Build aggregated bundles per session with unique tools per space
      const aggregatedMap = new Map();
      const ensureRec = (sid, seedEvt) => {
        if (!aggregatedMap.has(sid)) {
          aggregatedMap.set(sid, {
            sessionId: sid,
            datetime: seedEvt?.timestamp || new Date().toISOString(),
            managerTools: [],
            personalTools: [],
            projectTools: [],
            userEmail: seedEvt?.email || ''
          });
        }
        return aggregatedMap.get(sid);
      };
      const hasTool = (arr, tool) => arr.some(t => (t.tool || t) === tool);
      normalizedEvents.forEach(evt => {
        const sid = evt.sessionId || 'unknown';
        const rec = ensureRec(sid, evt);
        const tool = (evt?.details?.tool || evt.tool || '').toString();
        const type = (evt.sessionType || '').toLowerCase();
        if (!tool) return;
        // Store tool names only
        if (type === 'manager_space') {
          if (!hasTool(rec.managerTools, tool)) rec.managerTools.push(tool);
        } else if (type === 'personal_space') {
          if (!hasTool(rec.personalTools, tool)) rec.personalTools.push(tool);
        } else if (type === 'project_space') {
          if (!hasTool(rec.projectTools, tool)) rec.projectTools.push(tool);
        }
        // Keep earliest datetime
        if (evt.timestamp && rec.datetime && new Date(evt.timestamp) < new Date(rec.datetime)) {
          rec.datetime = evt.timestamp;
        }
        if (!rec.userEmail && evt.email) rec.userEmail = evt.email;
      });
      const aggregatedList = Array.from(aggregatedMap.values());

      // Merge with existing bundles, preserving the richest arrays
      const existingBundlesMap = new Map((state.sessionToolBundles || []).map(b => [b.sessionId, b]));
      const mergedBundles = aggregatedList.map(b => {
        const prev = existingBundlesMap.get(b.sessionId);
        if (!prev) return b;
        const union = (oldArr = [], newArr = []) => {
          const seen = new Set();
          const out = [];
          [...oldArr, ...newArr].forEach(x => {
            const name = x?.tool || x;
            if (!seen.has(name)) { seen.add(name); out.push(x); }
          });
          return out;
        };
        return {
          sessionId: b.sessionId,
          datetime: new Date(b.datetime) < new Date(prev.datetime || b.datetime) ? b.datetime : (prev.datetime || b.datetime),
          userEmail: b.userEmail || prev.userEmail || '',
          managerTools: union(prev.managerTools, b.managerTools),
          personalTools: union(prev.personalTools, b.personalTools),
          projectTools: union(prev.projectTools, b.projectTools)
        };
      });
      // Include any existing bundles not in the new list
      const newIds = new Set(mergedBundles.map(b => b.sessionId));
      const finalBundles = [...mergedBundles, ...(state.sessionToolBundles || []).filter(b => !newIds.has(b.sessionId))];
      return {
        ...state,
        isLoading: false,
        allSessions: action.payload.sessions || [],
        allEvents: normalizedEvents,
        allAggregatedEvents: aggregatedList,
        sessionToolBundles: finalBundles,
        userObj: action.payload.userObj || null,
        totalSessions: (action.payload.sessions || []).length,
        totalEvents: normalizedEvents.length,
        lastFetch: new Date().toISOString(),
        error: null
      };

    case GET_ALL_SESSIONS_FAIL:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        allSessions: [],
        allEvents: [],
  allAggregatedEvents: [],
        totalSessions: 0,
        totalEvents: 0
      };

    // Optimistic append of newly saved backend events
    case APPEND_BACKEND_EVENTS: {
      const newEvents = Array.isArray(action.payload) ? action.payload : [];
      if (newEvents.length === 0) return state;
      // Stable dedupe for allEvents (ignore timestamp)
      const byKey = new Map();
      const makeKey = (e) => `${e.sessionId || ''}|${(e.sessionType||'').toLowerCase()}|${(e.details?.tool||e.tool||'').toLowerCase()}`;
      [...(state.allEvents || []), ...newEvents].forEach(e => {
        const k = makeKey(e);
        if (!byKey.has(k)) byKey.set(k, e);
      });
      const allEvents = Array.from(byKey.values());

      // Update aggregated and bundles incrementally with unique tools
    const aggregatedMap = new Map((state.allAggregatedEvents || []).map(s => [s.sessionId, { ...s, managerTools: [...s.managerTools], personalTools: [...s.personalTools], projectTools: [...s.projectTools] }]));
    const bundlesMap = new Map((state.sessionToolBundles || []).map(s => [s.sessionId, { ...s, managerTools: [...s.managerTools], personalTools: [...s.personalTools], projectTools: [...s.projectTools] }]));
      const hasTool = (arr, tool) => arr.some(t => (t.tool || t) === tool);
      newEvents.forEach(evt => {
        const sid = evt.sessionId || 'unknown';
        const seed = {
          sessionId: sid,
          datetime: evt.timestamp || new Date().toISOString(),
          managerTools: [], personalTools: [], projectTools: [],
          userEmail: evt.email || ''
        };
        if (!aggregatedMap.has(sid)) aggregatedMap.set(sid, { ...seed });
        if (!bundlesMap.has(sid)) bundlesMap.set(sid, { ...seed });
        const upd = (rec) => {
          const tool = (evt?.details?.tool || evt.tool || '').toString();
          const type = (evt.sessionType || '').toLowerCase();
          if (!tool) return;
      // Store bundles as strings (tool names only)
      if (type === 'manager_space') { if (!hasTool(rec.managerTools, tool)) rec.managerTools.push(tool); }
      if (type === 'personal_space') { if (!hasTool(rec.personalTools, tool)) rec.personalTools.push(tool); }
      if (type === 'project_space') { if (!hasTool(rec.projectTools, tool)) rec.projectTools.push(tool); }
          if (!rec.userEmail && evt.email) rec.userEmail = evt.email;
        };
        upd(aggregatedMap.get(sid));
        upd(bundlesMap.get(sid));
      });
      return {
        ...state,
        allEvents,
        allAggregatedEvents: Array.from(aggregatedMap.values()),
        sessionToolBundles: Array.from(bundlesMap.values()),
        totalEvents: allEvents.length
      };
    }

    // === SESSION SYNC ===
    case SYNC_SESSION_LOADING:
      return {
        ...state,
        isSyncing: true,
        syncError: null
      };

    case SYNC_SESSION_SUCCESS:
      const newSession = action.payload.session || action.payload;
      const sessionExists = state.allSessions.some(s => s.sessionId === newSession.sessionId);
      
      return {
        ...state,
        isSyncing: false,
        syncError: null,
        allSessions: sessionExists 
          ? state.allSessions.map(s => 
              s.sessionId === newSession.sessionId ? newSession : s
            )
          : [...state.allSessions, newSession],
        syncedSessions: [...new Set([...state.syncedSessions, newSession.sessionId])],
        pendingSessions: state.pendingSessions.filter(id => id !== newSession.sessionId),
        totalSessions: sessionExists ? state.totalSessions : state.totalSessions + 1
      };

    case SYNC_SESSION_FAIL:
      return {
        ...state,
        isSyncing: false,
        syncError: action.payload
      };

    // === SESSION UPDATE ===
    case UPDATE_SESSION_LOADING:
      return {
        ...state,
        isUpdating: true,
        updateError: null
      };

    case UPDATE_SESSION_SUCCESS:
      const updatedSession = action.payload.session || action.payload;
      return {
        ...state,
        isUpdating: false,
        updateError: null,
        allSessions: state.allSessions.map(session =>
          session.sessionId === updatedSession.sessionId 
            ? { ...session, ...updatedSession }
            : session
        )
      };

    case UPDATE_SESSION_FAIL:
      return {
        ...state,
        isUpdating: false,
        updateError: action.payload
      };

    // === ERROR HANDLING ===
    case CLEAR_SESSION_ERRORS:
      return {
        ...state,
        error: null,
        syncError: null,
        updateError: null
      };

    // === HYDRATION FROM LOCAL CACHE ===
    case HYDRATE_SESSION_TOOL_BUNDLES: {
      const cached = Array.isArray(action.payload) ? action.payload : [];
      if (cached.length === 0) return state;
      const existingMap = new Map((state.sessionToolBundles || []).map(b => [b.sessionId, b]));
      const union = (oldArr = [], newArr = []) => {
        const seen = new Set();
        const out = [];
        [...oldArr, ...newArr].forEach(x => {
          const name = x?.tool || x;
          if (!seen.has(name)) { seen.add(name); out.push(x); }
        });
        return out;
      };
      const merged = cached.map(b => {
        const prev = existingMap.get(b.sessionId);
        if (!prev) return b;
        return {
          sessionId: b.sessionId,
          datetime: new Date(b.datetime) < new Date(prev.datetime || b.datetime) ? b.datetime : (prev.datetime || b.datetime),
          userEmail: b.userEmail || prev.userEmail || '',
          managerTools: union(prev.managerTools, b.managerTools),
          personalTools: union(prev.personalTools, b.personalTools),
          projectTools: union(prev.projectTools, b.projectTools)
        };
      });
      const newIds = new Set(merged.map(b => b.sessionId));
      const finalBundles = [...merged, ...(state.sessionToolBundles || []).filter(b => !newIds.has(b.sessionId))];
      return {
        ...state,
        sessionToolBundles: finalBundles
      };
    }

    default:
      return state;
  }
}

// === SELECTORS ===

/**
 * Get all sessions
 */
export const getAllSessionsSelector = (state) => state.sessions?.allSessions || [];

/**
 * Get all events
 */
export const getAllEventsSelector = (state) => state.sessions?.allEvents || [];

/**
 * Get aggregated events (legacy shape with arrays per session)
 */
export const getAllAggregatedEventsSelector = (state) => state.sessions?.allAggregatedEvents || [];

/**
 * Get per-session tool bundles (exact same shape as local currentTool)
 */
export const getSessionToolBundlesSelector = (state) => state.sessions?.sessionToolBundles || [];

/**
 * Get sessions for specific user
 */
export const getUserSessionsSelector = (state, userId) => 
  (state.sessions?.allSessions || []).filter(session => 
    session.userId === userId || session.email === userId
  );

/**
 * Get global statistics
 */
export const getSessionStatsSelector = (state) => ({
  totalSessions: state.sessions?.totalSessions || 0,
  totalEvents: state.sessions?.totalEvents || 0,
  syncedSessions: state.sessions?.syncedSessions?.length || 0,
  pendingSessions: state.sessions?.pendingSessions?.length || 0,
  lastFetch: state.sessions?.lastFetch
});

/**
 * Get loading status
 */
export const getLoadingStatusSelector = (state) => ({
  isLoading: state.sessions?.isLoading || false,
  isSyncing: state.sessions?.isSyncing || false,
  isUpdating: state.sessions?.isUpdating || false
});

/**
 * Get all errors
 */
export const getErrorsSelector = (state) => ({
  error: state.sessions?.error,
  syncError: state.sessions?.syncError,
  updateError: state.sessions?.updateError
});